import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { QUIZ_FEEDBACK_STYLES } from "./quiz-styles.js";
import { useQuizStyleVariant } from "./quiz-style-context.js";
import correctSound from "../../../assets/sounds/correct.wav.js";
import incorrectSound from "../../../assets/sounds/incorrect.wav.js";
import { AnimatePresence } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/components/AnimatePresence/index.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import Clock3 from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/clock-3.js";
import CheckCircle2 from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/check-circle-2.js";
import XCircle from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/x-circle.js";
import Lightbulb from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/lightbulb.js";
function QuizResultFeedback({
  isCorrect,
  show,
  correctAnswer: _correctAnswer,
  explanation,
  disableSound = false,
  isViewSolved = false
}) {
  const { t } = useTranslation("quiz");
  const variant = useQuizStyleVariant();
  const feedbackStyles = QUIZ_FEEDBACK_STYLES[variant];
  const hasPlayedSound = useRef(false);
  useEffect(() => {
    if (show && !isViewSolved && !hasPlayedSound.current && !disableSound && isCorrect !== null) {
      const audio = new Audio(isCorrect ? correctSound : incorrectSound);
      audio.volume = 0.5;
      audio.play().catch(() => {
      });
      hasPlayedSound.current = true;
    }
    if (!show) {
      hasPlayedSound.current = false;
    }
  }, [show, isViewSolved, isCorrect, disableSound]);
  return /* @__PURE__ */ jsx(AnimatePresence, { children: show && !isViewSolved && /* @__PURE__ */ jsxs(
    motion.div,
    {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 },
      className: "space-y-3",
      role: "alert",
      "aria-live": "polite",
      children: [
        isCorrect === null && /* @__PURE__ */ jsxs("div", { className: cn("p-4 rounded-xl flex items-center gap-3", feedbackStyles.submitted.container), children: [
          /* @__PURE__ */ jsx("div", { className: cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", feedbackStyles.submitted.icon), children: /* @__PURE__ */ jsx(Clock3, { className: "w-6 h-6 text-white" }) }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("p", { className: cn("text-lg font-bold", feedbackStyles.submitted.title), children: t("feedback.submitted", "제출 완료") }),
            /* @__PURE__ */ jsx("p", { className: cn("text-base", feedbackStyles.submitted.text), children: t("feedback.submittedMessage", "선생님이 답안을 확인하고 있어요.") })
          ] })
        ] }),
        isCorrect !== null && /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "p-4 rounded-xl flex items-center gap-3",
              isCorrect ? feedbackStyles.correct.container : feedbackStyles.wrong.container
            ),
            children: isCorrect ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("div", { className: cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", feedbackStyles.correct.icon), children: /* @__PURE__ */ jsx(CheckCircle2, { className: "w-6 h-6 text-white" }) }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("p", { className: cn("text-lg font-bold", feedbackStyles.correct.title), children: t("feedback.correct") }),
                /* @__PURE__ */ jsx("p", { className: cn("text-base", feedbackStyles.correct.text), children: t("feedback.correctMessage") })
              ] })
            ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("div", { className: cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", feedbackStyles.wrong.icon), children: /* @__PURE__ */ jsx(XCircle, { className: "w-6 h-6 text-white" }) }),
              /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsx("p", { className: cn("text-lg font-bold", feedbackStyles.wrong.title), children: t("feedback.incorrect") }) })
            ] })
          }
        ),
        explanation && /* @__PURE__ */ jsx(
          motion.div,
          {
            initial: { opacity: 0, height: 0 },
            animate: { opacity: 1, height: "auto" },
            className: "p-4 rounded-2xl bg-linear-to-r from-gray-50 to-slate-50 border border-gray-200",
            children: /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
              /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center shrink-0", children: /* @__PURE__ */ jsx(Lightbulb, { className: "w-4 h-4 text-white" }) }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("p", { className: "text-lg font-semibold text-gray-700 mb-1", children: t("feedback.explanation") }),
                /* @__PURE__ */ jsx("p", { className: "text-base text-gray-600 leading-relaxed", children: explanation })
              ] })
            ] })
          }
        )
      ]
    }
  ) });
}
export {
  QuizResultFeedback
};
//# sourceMappingURL=quiz-result-feedback.js.map
